import { AgGridHelperService } from '@products/fund-listing/services/ag-grid-helper.service';
import { ColGroupDef } from '@ag-grid-community/core';
import { PPSSState } from '@types';
import { PpssPerformanceComponent } from '@products/fund-listing/ppss/ppss-performance/ppss-performance.component';
import { PpssRatingsComponent } from '@products/fund-listing/ppss/ppss-ratings/ppss-ratings.component';
import { PpssYieldsComponent } from '@products/fund-listing/ppss/ppss-yields/ppss-yields.component';
import { TranslateService } from '@shared/translate/translate.service';

/**
 * Return performance column definitions for US CEF.
 * @param ppssTab ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 */
export function getPerformanceColumnDefsForUS_CEF(
  ppssTab: PpssPerformanceComponent,
  state: PPSSState,
  agGridHelperService: AgGridHelperService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: 'FUND',
      headerGroupComponent: 'calcTypeGroupHeaderComponent',
      headerGroupComponentParams: {
        parentComponent: ppssTab,
        configurationName: ppssTab.productType,
        calcType: ppssTab.calcType,
      },
      children: [
        agGridHelperService.getFavFundColDef(ppssTab),
        agGridHelperService.getFundNameColDef(false),
      ],
    },
    {
      groupId: 'NAV',
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => ppssTab.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavValueColDef({
          width: 107,
        }),
        agGridHelperService.getMarketPriceValueColDef({
          width: 107,
        }),
        agGridHelperService.getNavChangeValueColDef({
          width: 107,
        }),
        agGridHelperService.getMarketPriceChangeValueColDef({
          width: 107,
        }),
        agGridHelperService.getNavYTDReturnColDef(),
        agGridHelperService.getMarketPriceYTDReturnColDef(),
        agGridHelperService.getDistributionRateColDef(
          { width: 85, minWidth: 50 },
          null,
          ppssTab.isSiteIntl,
          ppssTab.productType
        ),
        agGridHelperService.getPremiumDiscountColDef({
          width: 100,
        }),
      ],
    },
    {
      groupId: 'PERFORMANCE',
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: {
        perfTable: ppssTab,
      },
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          state.config.suppressDetails,
          { width: 100 }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef({ width: 100 }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef({ width: 100 }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef({ width: 100 }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          state.config.suppressDetails,
          {
            width: 115,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          state.config.suppressDetails,
          { width: 100 }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef({ width: 100 }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef({ width: 100 }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef({ width: 100 }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          state.config.suppressDetails,
          {
            width: 115,
          }
        ),
        agGridHelperService.getFundDocumentColDef({
          width: 80,
        }),
      ],
    },
  ];
  ppssTab.calcTypeParams = colGroupDefs.find(
    (colGroupDef: ColGroupDef): boolean => colGroupDef.groupId === 'FUND'
  ).headerGroupComponentParams;
  ppssTab.performanceParams = colGroupDefs.find(
    (colGroupDef: ColGroupDef): boolean => colGroupDef.groupId === 'PERFORMANCE'
  ).headerGroupComponentParams;
  return colGroupDefs;
}

/**
 * Return default yield column definitions for intl.
 * @param ppssTab ppss tab component
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getYieldColumnDefsForUS_CEF(
  ppssTab: PpssYieldsComponent,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: 'FUND',
      children: [
        agGridHelperService.getFavFundColDef(ppssTab),
        agGridHelperService.getFundNameColDef(false),
      ],
    },
    {
      groupId: 'DISTRIBUTION',
      headerName: `${translateService.instant(
        'products.distribution-rate-date'
      )} ${ppssTab.navAsOfDate}`,
      children: [
        agGridHelperService.getDailyVolumeColDef({
          maxWidth: 200,
        }),
      ],
    },
  ];
  ppssTab.calcTypeParams = colGroupDefs.find(
    (colGroupDef: ColGroupDef): boolean => colGroupDef.groupId === 'FUND'
  ).headerGroupComponentParams;
  return colGroupDefs;
}

/**
 * Return default rating column definitions for intl.
 * @param ppssTab ppss tab component
 * @param agGridHelperService ag grid helper service instance
 */
export function getRatingColumnDefsForUS_CEF(
  ppssTab: PpssRatingsComponent,
  agGridHelperService: AgGridHelperService
): ColGroupDef[] {
  return [
    {
      groupId: 'FUND',
      children: [agGridHelperService.getFundNameColDef(false)],
    },
    {
      groupId: 'MORNINGSTARRATINGS',
      headerGroupComponent: 'ratingsGroupHeaderComponent',
      headerClass: 'ag-header-group-cell--noflex',
      headerGroupComponentParams: {
        ratingsAsOfDate: ppssTab.ratingsAsOfDate,
      },
      children: [
        agGridHelperService.getMorningStarCategoryColDef(),
        agGridHelperService.getMorningStarOverallRatingColDef(false, {
          flex: 0,
        }),
        agGridHelperService.getMorningStar3yrRatingColDef(),
        agGridHelperService.getMorningStar5yrRatingColDef(),
        agGridHelperService.getMorningStar10yrRatingColDef(),
      ],
    },
  ];
}
